import { getPlateDesignOption } from '@/common/queries/selections/plateDesign/plateDesignOption';
import { getPlateSampleOption } from '@/common/queries/selections/plateSample/plateSampleOption';
import { getPlateTypeOption } from '@/common/queries/selections/plateType/plateTypeOption';
import { infiniteGetVehicleSystemOption } from '@/common/queries/selections/vehicleSystem/vehicleSystemOption';
import { getVehicleTypeOption } from '@/common/queries/selections/vehicleType/vehicleTypeOption';
import { vehicleUseOption } from '@/common/queries/selections/vehicleUse/vehicleUseOption';
import { VehicleInformationSelectionsType } from '@/modules/adjusterReportModules/IncidentParticipants/data/VehicleInformationSelections/useVehicleInformationSelections.type';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

const useVehicleInformationSelections = ({
  plateTypesDependencies,
  vehicleSystemsDependencies,
  vehicleUsesDependencies,
  plateDesignDependencies,
  plateSamplesDependencies,
}: VehicleInformationSelectionsType) => {
  const { data: vehicleTypes } = useQuery(getVehicleTypeOption());
  const { data: vehicleUses } = useQuery({
    ...vehicleUseOption({
      insurance: vehicleUsesDependencies?.insurance!,
      type: vehicleUsesDependencies?.type!,
    }),
    enabled:
      vehicleUsesDependencies &&
      !!vehicleUsesDependencies?.insurance &&
      !!vehicleUsesDependencies.type,
  });

  const {
    data: vehicleSystems,
    fetchNextPage: fetchVehicleSystemsNextPage,
    isFetching: isVehicleSystemsPending,
  } = useInfiniteQuery({
    ...infiniteGetVehicleSystemOption(vehicleSystemsDependencies!),
    enabled:
      vehicleSystemsDependencies &&
      !!vehicleSystemsDependencies?.insurance &&
      !!vehicleSystemsDependencies.type,
  });

  const { data: plateDesigns } = useQuery({
    ...getPlateDesignOption(plateDesignDependencies!),
    enabled:
      plateDesignDependencies &&
      !!plateDesignDependencies?.type &&
      !!plateDesignDependencies?.insurance,
  });
  const { data: plateSamples } = useQuery({
    ...getPlateSampleOption(plateSamplesDependencies!),
    enabled:
      plateSamplesDependencies &&
      !!plateSamplesDependencies?.type &&
      !!plateSamplesDependencies?.insurance &&
      !!plateSamplesDependencies?.plate_design &&
      !!plateSamplesDependencies?.plate_type,
  });
  const { data: plateTypes } = useQuery({
    ...getPlateTypeOption(plateTypesDependencies!),
    enabled:
      plateTypesDependencies &&
      !!plateTypesDependencies?.type &&
      !!plateTypesDependencies?.insurance,
  });

  return {
    vehicleTypes: vehicleTypes?.map((item) => ({
      label: item.fa_name,
      value: item.id,
    })),
    vehicleUses: vehicleUses?.map((item) => ({
      label: item.name,
      value: item.id,
    })),
    vehicleSystems: vehicleSystems?.pages.flatMap((page) =>
      page.results.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    ),
    fetchVehicleSystemsNextPage,
    isVehicleSystemsPending,
    plateDesigns: plateDesigns?.map((item) => ({
      label: item.fa_const,
      value: item.const,
    })),
    plateTypes: plateTypes?.map((item) => ({
      label: item.name,
      value: item.id,
    })),
    plateSamples: plateSamples?.map((item) => ({
      label: item.title,
      value: item.id,
    })),
  };
};

export default useVehicleInformationSelections;
